/**
 * Sentry Error Tracking Module for Web
 *
 * Initialize Sentry for error tracking and performance monitoring.
 * Import this module in main.tsx BEFORE any other imports.
 *
 * Features:
 * - Error tracking with source maps
 * - Performance monitoring (10% sampling)
 * - React Router integration for page tracking
 * - React component error boundaries
 */

import * as Sentry from '@sentry/react';

// Environment detection
const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;

// Sentry DSN from environment variable
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Initialize Sentry with configuration
 *
 * - Development: Captures but doesn't send (unless VITE_SENTRY_DEBUG=true)
 * - Production: Sends errors with 10% performance sampling
 */
Sentry.init({
  dsn: SENTRY_DSN,

  // Environment tag
  environment: isProd ? 'production' : 'development',

  // Release version for tracking
  release: import.meta.env.VITE_APP_VERSION || '0.0.1',

  // PII collection - match backend behavior
  sendDefaultPii: true,

  // Performance Monitoring
  // Capture 10% of transactions for performance monitoring
  tracesSampleRate: isProd ? 0.1 : 1.0,

  // Capture 10% of sessions for Session Replay (production only)
  replaysSessionSampleRate: isProd ? 0.1 : 0,

  // Capture 100% of sessions with errors for Session Replay
  replaysOnErrorSampleRate: 1.0,

  // React Router integration
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

  // Don't send events in development unless explicitly enabled
  // Also disabled if DSN is not configured
  enabled: !!SENTRY_DSN && (isProd || import.meta.env.VITE_SENTRY_DEBUG === 'true'),

  // Filter out common client-side errors that aren't actionable
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Skip network errors (user's connectivity issues)
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return null;
    }

    // Skip cancelled requests
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }

    // Skip ResizeObserver errors (browser quirks)
    if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
      return null;
    }

    return event;
  },

  // Log errors to console in development
  debug: isDev,
});

// Set common tags
Sentry.setTag('platform', 'web');
Sentry.setTag('release_stage', 'mvp');

/**
 * Set user context after authentication
 * @param userId - Unique user identifier
 * @param email - Optional user email
 */
export function setSentryUser(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email,
  });
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Capture an exception with optional context
 * @param error - Error to capture
 * @param context - Optional additional context
 */
export function captureException(error: Error | unknown, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a message with severity level
 * @param message - Message to capture
 * @param level - Severity level
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 * @param breadcrumb - Breadcrumb data
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

// Export Sentry components for use in React
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export raw Sentry for advanced usage
export { Sentry };

export default Sentry;
